import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import {
  lineupStyleClassName,
  type LineupStyle,
} from '@sorare/core/src/components/lobby/ContenderLayout/types';

const Root = styled(Bezel)`
  height: 100%;
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: var(--unit);

  &.isInline,
  &.forced_inline {
    background:
      var(--background-gradient),
      linear-gradient(
          var(--c-black) 0%,
          rgba(var(--c-rgb-black), 0.4) 50%,
          var(--c-black) 100%
        )
        padding-box,
      var(--background-url);

    background-size: cover;
    background-position: center;
    background-origin: border-box;
  }

  &.spread {
    background:
      var(--background-gradient),
      linear-gradient(var(--c-black) 0%, transparent 50%, var(--c-black) 100%),
      radial-gradient(75% 75% at 50% 50%, transparent 50%, var(--c-black) 100%),
      var(--background-url);
    background-size: cover, cover, cover, cover;
    background-position:
      center,
      center,
      center 25px,
      center;
    background-origin: border-box;
  }
`;

export const LineupBezelWrapper = ({
  children,
  color,
  backgroundUrl,
  lineupStyle,
}: {
  children: ReactNode;
  color?: string;
  backgroundUrl?: string | null;
  lineupStyle?: LineupStyle;
}) => {
  return (
    <Root
      borderColor={
        color
          ? `linear-gradient(to bottom, rgba(${color}, 1), var(--c-nd-100) var(--triple-unit), var(--c-nd-100) calc(100% - var(--triple-unit)), rgba(${color}, 1))`
          : 'var(--c-nd-150)'
      }
      className={classNames(lineupStyleClassName(lineupStyle))}
      style={
        {
          '--background-url': backgroundUrl
            ? `url(${proxyUrl(backgroundUrl, {
                cropWidth: 320,
              })})`
            : 'none',
          '--background-gradient': color
            ? `
                linear-gradient(
                  to bottom,
                  rgba(${color}, 0.2),
                  transparent var(--quadruple-unit),
                  transparent calc(100% - var(--triple-unit)),
                  rgba(${color}, 0.2)
                )
              `
            : 'linear-gradient(rgba(0, 0, 0, 0))',
        } as CSSProperties
      }
    >
      {children}
    </Root>
  );
};
